import {
  Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, computed, inject, signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { EmojiSearch, PickerModule } from '@ctrl/ngx-emoji-mart';

import { MensagemModel } from '../../../shared/model/chat/mensagens/mensagem-model';
import { MsgService } from '../../../shared/service/msg.service';
import { PlayerAudioComponent } from '../balao-mensagem/player-audio/player-audio.component';
import { GravadorAudio } from './utils/gravador-audio.utils';
import { InfoArquivo } from '../../models/info-arquivo.model';
import { MatTooltip } from '@angular/material/tooltip';
import { animacaoPop, animacaoSubir, animacaoSurgir } from '../../utils/chat-animacoes.utils';
import { EmojiBuscaPtService } from '../../../shared/service/emoji-busca-pt.service';
import { I18N_EMOJI } from '../../utils/i18n-emoji.constantes';
import { MethodsUtils } from '../../../shared/util/methods.utils';
import { ArquivoAnexado } from './models/arquivo-anexado.model';
import { EnvioArquivosChat } from './models/envio-arquivos-chat.model';
import { CHAVE_RASCUNHO, EXTENSOES_PERMITIDAS } from './utils/conversa-rodape.constantes';

@Component({
  selector: 'app-conversa-rodape',
  standalone: true,
  imports: [FormsModule, MatIcon, MatTooltip, PickerModule, PlayerAudioComponent],
  providers: [{ provide: EmojiSearch, useClass: EmojiBuscaPtService }],
  animations: [animacaoPop, animacaoSubir, animacaoSurgir],
  templateUrl: './conversa-rodape.component.html',
  styleUrl: './conversa-rodape.component.css'
})
export class ConversaRodapeComponent implements OnChanges, OnDestroy {
  protected readonly acceptArquivos = EXTENSOES_PERMITIDAS.map(e => `.${e}`).join(',');
  protected readonly i18nEmoji = I18N_EMOJI;

  @Input() sessaoAberta = false;
  @Input() podeEnviar = false;
  @Input() motivoBloqueio = '';
  @Input() bloqueado = false;
  @Input() ehInstagram = false;
  @Input() carregando = false;
  @Input() numeroContato = '';
  @Input() nomeContato = '';
  @Input() mensagemResposta?: MensagemModel;

  @Output() enviarTexto = new EventEmitter<string>();
  @Output() enviarArquivos = new EventEmitter<EnvioArquivosChat>();
  @Output() cancelarResposta = new EventEmitter<void>();
  @Output() abrirTemplates = new EventEmitter<void>();

  @ViewChild('campoTexto') campoTexto?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('inputArquivo') inputArquivo?: ElementRef<HTMLInputElement>;
  @ViewChild(PlayerAudioComponent) playerPrevia?: PlayerAudioComponent;

  texto = '';
  arquivos = signal<ArquivoAnexado[]>([]);
  emojiAberto = signal(false);

  readonly gravador = new GravadorAudio();
  readonly gravando = computed(() => this.gravador.estado() !== 'parado');
  readonly tempoGravacao = computed(() => MethodsUtils.formatarTempoMidia(this.gravador.duracaoMs() / 1000));

  private posicaoCursor: number | null = null;
  private focarAoLiberar = true;

  private readonly msgService = inject(MsgService);
  private readonly elementRef = inject(ElementRef);

  ngOnChanges(changes: SimpleChanges): void {
    const trocaContato = changes['numeroContato'];
    if (trocaContato) {
      if (!trocaContato.firstChange) {
        this.salvarRascunho(trocaContato.previousValue);
        this.limparTudo();
      }
      this.texto = this.lerRascunho(this.numeroContato);
      this.posicaoCursor = null;
      this.focarAoLiberar = true;
      setTimeout(() => this.ajustarAltura(), 0);
    }
    if (changes['mensagemResposta']?.currentValue) {
      this.focar();
    }
    if (this.focarAoLiberar && this.podeEnviar && !this.carregando) {
      this.focarAoLiberar = false;
      if (!window.matchMedia('(pointer: coarse)').matches) this.focar();
    }
  }

  ngOnDestroy(): void {
    this.salvarRascunho(this.numeroContato);
    this.gravador.descartar();
    this.revogarPreviews(this.arquivos());
  }

  @HostListener('document:click', ['$event'])
  aoClicarFora(event: MouseEvent): void {
    const host = this.elementRef.nativeElement as HTMLElement;
    const alvo = event.target as Node;
    if (this.emojiAberto() && !host.contains(alvo)) {
      this.emojiAberto.set(false);
    }
  }

  get temConteudo(): boolean {
    return this.texto.trim().length > 0 || this.arquivos().length > 0;
  }

  resumoResposta(mensagem: MensagemModel): string {
    return MethodsUtils.resumoMensagem(mensagem);
  }

  aoDigitar(): void {
    this.ajustarAltura();
    this.salvarRascunho(this.numeroContato);
  }

  private lerRascunho(numero: string): string {
    if (!numero) return '';
    return MethodsUtils.lerStorage(CHAVE_RASCUNHO + numero) ?? '';
  }

  private salvarRascunho(numero: string): void {
    if (!numero) return;
    const texto = this.texto;
    if (texto.trim()) MethodsUtils.salvarStorage(CHAVE_RASCUNHO + numero, texto);
    else MethodsUtils.removerStorage(CHAVE_RASCUNHO + numero);
  }

  lembrarCursor(): void {
    this.posicaoCursor = this.campoTexto?.nativeElement.selectionStart ?? null;
  }

  aoPressionarTecla(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
    if (window.matchMedia('(pointer: coarse)').matches) return;
    event.preventDefault();
    this.enviar();
  }

  aoColar(event: ClipboardEvent): void {
    const arquivos = Array.from(event.clipboardData?.files ?? []);
    if (arquivos.length > 0) {
      event.preventDefault();
      this.adicionarArquivos(arquivos);
    }
  }

  enviar(): void {
    if (!this.validarPodeEnviar()) return;
    if (!this.temConteudo) {
      this.msgService.msgAdvertencia('Escreva a mensagem!');
      return;
    }

    const texto = this.texto.trim();
    const arquivos = this.arquivos();
    if (arquivos.length > 0) {
      this.enviarArquivos.emit({ arquivos: arquivos.map(a => a.file), texto });
      this.revogarPreviews(arquivos);
      this.arquivos.set([]);
    } else {
      this.enviarTexto.emit(texto);
    }
    this.texto = '';
    this.salvarRascunho(this.numeroContato);
    this.posicaoCursor = null;
    this.emojiAberto.set(false);
    setTimeout(() => this.ajustarAltura(), 0);
    this.focar();
  }

  alternarEmoji(): void {
    this.lembrarCursor();
    this.emojiAberto.update(v => !v);
  }

  adicionarEmoji(event: any): void {
    const emoji: string | undefined = event?.emoji?.native;
    if (!emoji) return;
    const posicao = this.posicaoCursor ?? this.texto.length;
    this.texto = this.texto.slice(0, posicao) + emoji + this.texto.slice(posicao);
    this.posicaoCursor = posicao + emoji.length;
    this.salvarRascunho(this.numeroContato);
    setTimeout(() => this.ajustarAltura(), 0);
  }

  focar(): void {
    setTimeout(() => this.campoTexto?.nativeElement.focus(), 30);
  }

  private ajustarAltura(): void {
    const el = this.campoTexto?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 110)}px`;
  }

  private validarPodeEnviar(): boolean {
    if (this.podeEnviar) return true;
    if (this.motivoBloqueio) this.msgService.msgAdvertencia(this.motivoBloqueio);
    return false;
  }

  abrirSeletorArquivo(): void {
    if (!this.validarPodeEnviar()) return;
    this.inputArquivo?.nativeElement.click();
  }

  aoSelecionarArquivos(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.adicionarArquivos(Array.from(input.files ?? []));
    input.value = '';
  }

  adicionarArquivos(files: File[]): void {
    if (!files.length || !this.validarPodeEnviar()) return;
    const novos: ArquivoAnexado[] = [];
    files.forEach(file => {
      const extensao = MethodsUtils.extensaoDoArquivo(file.name);
      if (!EXTENSOES_PERMITIDAS.includes(extensao)) {
        this.msgService.msgAdvertencia(`O arquivo "${file.name}" tem um tipo não permitido.`);
        return;
      }
      const ehImagem = file.type.startsWith('image');
      novos.push({ file, ehImagem, preview: ehImagem ? URL.createObjectURL(file) : null });
    });
    if (novos.length) {
      this.arquivos.set([...this.arquivos(), ...novos]);
      this.emojiAberto.set(false);
      this.focar();
    }
  }

  infoDoArquivo(arquivo: File): InfoArquivo {
    return MethodsUtils.infoArquivo(MethodsUtils.extensaoDoArquivo(arquivo.name));
  }

  removerArquivo(indice: number): void {
    const lista = [...this.arquivos()];
    const [removido] = lista.splice(indice, 1);
    this.revogarPreviews(removido ? [removido] : []);
    this.arquivos.set(lista);
  }

  private revogarPreviews(arquivos: ArquivoAnexado[]): void {
    arquivos.forEach(a => a.preview && URL.revokeObjectURL(a.preview));
  }

  async iniciarGravacao(): Promise<void> {
    if (!this.validarPodeEnviar()) return;
    this.emojiAberto.set(false);
    const erro = await this.gravador.iniciar();
    if (erro) this.msgService.msgAdvertencia(erro);
  }

  pausarOuRetomar(): void {
    if (this.gravador.estado() === 'gravando') {
      this.gravador.pausar();
    } else if (this.gravador.estado() === 'pausado') {
      this.pararPrevia();
      this.gravador.retomar();
    }
  }

  pararPrevia(): void {
    this.playerPrevia?.pausarReproducao();
  }

  descartarGravacao(): void {
    this.pararPrevia();
    this.gravador.descartar();
  }

  async enviarGravacao(): Promise<void> {
    this.pararPrevia();
    const arquivo = await this.gravador.finalizar();
    if (!arquivo) {
      this.msgService.msgAdvertencia('Áudio muito curto. Segure por pelo menos 1 segundo.');
      return;
    }
    this.enviarArquivos.emit({ arquivos: [arquivo], texto: '' });
  }

  private limparTudo(): void {
    this.texto = '';
    this.posicaoCursor = null;
    this.revogarPreviews(this.arquivos());
    this.arquivos.set([]);
    this.emojiAberto.set(false);
    this.descartarGravacao();
    setTimeout(() => this.ajustarAltura(), 0);
  }
}
